import {
	faDatabase,
	faSitemap,
	faWandMagicSparkles,
} from '@fortawesome/pro-solid-svg-icons';
import { ObjectService } from '@supply-mover-2-0/core';
import { useEffect, useState } from 'react';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { Input } from '../../../../components/forms/Input';
import { Select } from '../../../../components/forms/Select';
import { SelectSearch } from '../../../../components/forms/SelectSearch';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import { useInjectedFields } from '../../../registry/InjectedFieldRegistry';
import type {
	DataSourceType,
	FieldConfiguration,
	ListBuilderConfiguration,
} from '../../../types';
import { useListBuilderContext } from '../../core/ListBuilderProvider';
import { TemplateBuilder } from '../../template-builder/TemplateBuilder';
import { ConditionalDataConfigurator } from './ConditionalDataConfigurator';

interface DataSourceConfiguratorProps {
	field: FieldConfiguration;
	configuration?: ListBuilderConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
	onPreviewValueChange?: (value: string) => void;
	isModalOpen?: boolean;
}

export function DataSourceConfigurator({
	field,
	configuration,
	onUpdateField,
	onPreviewValueChange,
	isModalOpen = false,
}: DataSourceConfiguratorProps) {
	const colors = useThemeColors();
	const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
	const { listKey } = useListBuilderContext();
	const injectedFields = useInjectedFields(listKey);
	const [customFields, setCustomFields] = useState<
		Array<{
			fieldName: string;
			fieldLabel: string;
			objectTable: string;
			objectLabel: string;
		}>
	>([]);

	useEffect(() => {
		if (configuration?.objects == null || configuration.objects.length === 0)
			return;

		const fetchCustomFields = async () => {
			const cfFields: typeof customFields = [];
			for (const obj of configuration.objects) {
				const result = await ObjectService.getObjectFields(obj.objectTable);
				if (result != null) {
					for (const field of result) {
						if (field.fieldName.startsWith('cf:')) {
							cfFields.push({
								fieldName: field.fieldName,
								fieldLabel: field.fieldLabel,
								objectTable: obj.objectTable,
								objectLabel: obj.objectLabel,
							});
						}
					}
				}
			}
			setCustomFields(cfFields);
		};
		void fetchCustomFields();
	}, [configuration?.objects]);

	// Get available fields from configuration
	const getAvailableFields = () => {
		if (!configuration?.objects || configuration.objects.length === 0) {
			return [];
		}

		const fields: Array<{
			fieldName: string;
			fieldLabel: string;
			objectTable: string;
			objectLabel: string;
		}> = [];

		configuration.objects.forEach((obj) => {
			obj.selectedFields.forEach((field) => {
				fields.push({
					fieldName: field.fieldName,
					fieldLabel: field.fieldLabel,
					objectTable: obj.objectTable,
					objectLabel: obj.objectLabel,
				});
			});
		});

		return fields;
	};

	const availableFields = getAvailableFields();

	// Combine database fields, custom fields, and injected fields
	const allAvailableFields = [
		...availableFields,
		...customFields,
		...injectedFields.map((field) => ({
			fieldName: field.fieldName,
			fieldLabel: field.displayName,
			objectTable: listKey || configuration?.objects?.[0]?.objectTable || '',
			objectLabel: `${configuration?.objects?.[0]?.objectLabel || 'Object'} (Injected)`,
		})),
	];

	return (
		<>
			<Card className="mb-4">
				<div className="p-4">
					<div className="flex items-center mb-3">
						<Icon
							icon={faDatabase}
							size="sm"
							color={colors.values.textPrimary}
							className="mr-2"
						/>
						<Text className="text-base font-semibold">Data Source</Text>
					</div>

					{/* Data Source Type Selection */}
					<div className="mb-3">
						<Select
							value={field.dataConfig.type}
							options={[
								{ value: 'field' as DataSourceType, label: 'Database Field' },
								{
									value: 'custom_text' as DataSourceType,
									label: 'Custom Text',
								},
								{ value: 'template' as DataSourceType, label: 'Template' },
								{
									value: 'conditional' as DataSourceType,
									label: 'Conditional',
								},
							]}
							onChange={(value) => {
								const newDataConfig = { type: value as DataSourceType };
								onUpdateField({ dataConfig: newDataConfig });
							}}
						/>
					</div>

					{/* Type-specific configuration */}
					{field.dataConfig.type === 'field' && (
						<div>
							<Text className="block text-xs font-medium mb-1" as="label">
								Select Field
							</Text>
							<div className="mb-2">
								<SelectSearch
									mode="static"
									groups={(() => {
										const grouped = new Map<
											string,
											Array<{ value: string | number; label: string }>
										>();
										allAvailableFields.forEach((f) => {
											const key = f.objectLabel;
											if (!grouped.has(key)) {
												grouped.set(key, []);
											}
											grouped.get(key)?.push({
												value: `${f.objectTable}:${f.fieldName}`,
												label: f.fieldLabel,
											});
										});
										return Array.from(grouped.entries()).map(
											([label, options]) => ({
												key: label,
												label,
												options,
											}),
										);
									})()}
									value={(() => {
										if (field.dataConfig.field == null) return undefined;
										const fieldKey = `${field.dataConfig.field.objectTable}:${field.dataConfig.field.fieldName}`;
										const exists = allAvailableFields.some(
											(f) => `${f.objectTable}:${f.fieldName}` === fieldKey,
										);
										return exists ? fieldKey : undefined;
									})()}
									onChange={(value) => {
										const separatorIndex = value.indexOf(':');
										const objectTable = value.substring(0, separatorIndex);
										const fieldName = value.substring(separatorIndex + 1);
										onUpdateField({
											dataConfig: {
												...field.dataConfig,
												field: { objectTable, fieldName },
											},
										});
									}}
									placeholder="Search fields..."
									size="sm"
								/>
							</div>
						</div>
					)}

					{field.dataConfig.type === 'custom_text' && (
						<div>
							<Text className="block text-xs font-medium mb-1" as="label">
								Text Value
							</Text>
							<Input
								type="text"
								value={field.dataConfig.customText || ''}
								onChange={(e) => {
									const text = e.target.value;
									onUpdateField({
										dataConfig: {
											...field.dataConfig,
											customText: text,
										},
									});
									if (onPreviewValueChange) {
										onPreviewValueChange(text);
									}
								}}
								placeholder="Enter custom text..."
								size="sm"
							/>
						</div>
					)}

					{field.dataConfig.type === 'template' && (
						<div>
							<button
								onClick={() => {
									setShowTemplateBuilder(true);
								}}
								className={cn(
									'w-full p-3 border rounded-md transition-colors',
									colors.classes.borderDefault,
									colors.classes.bgSecondary,
									colors.classes.bgPrimaryHover,
								)}
							>
								<div className="flex items-center justify-between">
									<div className="flex-1 text-left">
										<Text className="text-sm font-medium mb-1">
											Template Configuration
										</Text>
										{field.dataConfig.template ? (
											<Text className="text-xs line-clamp-2" color="secondary">
												{field.dataConfig.template}
											</Text>
										) : (
											<Text className="text-xs italic" color="muted">
												Click to configure template...
											</Text>
										)}
									</div>
									<Icon
										icon={faWandMagicSparkles}
										size="sm"
										color={colors.values.primary}
									/>
								</div>
							</button>
						</div>
					)}

					{field.dataConfig.type === 'conditional' && (
						<div>
							<div className="flex items-center mb-3">
								<Icon
									icon={faSitemap}
									size="sm"
									color={colors.values.textPrimary}
									className="mr-2"
								/>
								<Text className="text-sm font-medium">
									Conditional Data Configuration
								</Text>
							</div>
							<ConditionalDataConfigurator
								field={field}
								configuration={configuration}
								onUpdateField={onUpdateField}
								availableFields={allAvailableFields}
							/>
						</div>
					)}

					{/* Default Value for all data source types */}
					<div
						className={cn('mt-4 pt-4 border-t', colors.classes.borderDefault)}
					>
						<Text className="block text-xs font-medium mb-1" as="label">
							Default Value (when null/empty)
						</Text>
						<Input
							type="text"
							value={field.dataConfig.defaultValue ?? ''}
							onChange={(e) => {
								onUpdateField({
									dataConfig: {
										...field.dataConfig,
										defaultValue: e.target.value,
									},
								});
							}}
							placeholder="e.g., -, N/A, Not set"
							size="sm"
						/>
						<Text className="text-xs mt-1" color="secondary">
							This value will be displayed when the field is null or empty
						</Text>
					</div>
				</div>
			</Card>

			{/* Template Builder Modal */}
			{showTemplateBuilder && (
				<div className="fixed inset-0 z-50 flex items-center justify-center">
					<div
						className={cn('absolute inset-0', colors.classes.bgOverlay)}
						onClick={() => {
							setShowTemplateBuilder(false);
						}}
					/>
					<div
						className={cn(
							'relative w-full max-w-2xl mx-4 rounded-lg shadow-xl',
							colors.classes.bgPrimary,
						)}
					>
						<TemplateBuilder
							value={field.dataConfig.template || ''}
							onChange={(template) => {
								// Extract fields used in template for dependency tracking
								const templateFields = availableFields
									.filter((f) => template.includes(`{${f.fieldName}}`))
									.map((f) => f.fieldName);

								onUpdateField({
									dataConfig: {
										...field.dataConfig,
										template,
										templateFields,
									},
								});
								setShowTemplateBuilder(false);
							}}
							availableFields={availableFields}
							onClose={() => {
								setShowTemplateBuilder(false);
							}}
						/>
					</div>
				</div>
			)}
		</>
	);
}
